import { Component, OnInit } from '@angular/core';
import { JsonService } from '../_services/json.service';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogService } from '../dialog/dialog.service';
import { TranslateService } from '@ngx-translate/core';
import { LoadingService } from '../_services/loading.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { finalize } from 'rxjs';
import { ConfirmationDialogService } from '../_services/confirmation-dialog.service';

const DIRECTORY_FIXED_INDEXES = {
  IndexYear: 5,
  IndexProvider: 8,
  IndexMonth: 6,
  IndexPrescriptionBatch: 7,
  IndexRegion: 9,
  IndexPDV: 10
};

@Component({
  selector: 'app-directory-detail',
  templateUrl: './directory-detail.component.html',
  styleUrl: './directory-detail.component.scss'
})
export class DirectoryDetailComponent implements OnInit {
  public jsonPaises: Array<any> = [];
  public listaRegiao: Array<any> = [];
  public uid: string = '';
  public pathSelected: string = '';
  public countrySelected: string = '';
  public regionSelected: string = '';
  public loading: boolean = false;
  public formDiretorio: FormGroup;
  public isCreateMode: boolean = false;
  public selectedPdvFile: File | null = null;
  public selectedPdvFileCount: number | null = null;
  public uploadingPdv: boolean = false;
  public registeredCountryIds: Set<string> = new Set<string>();
  public mesesFallback: string[] = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
  public mesesDisponiveis: string[] = [];

  public filtro: any = {
    pais: '',
    pdv: '',
    ano: '',
    mes: '',
    regiao: '',
    provedor: ''
  };

  constructor(
    private jsonService: JsonService,
    private route: ActivatedRoute,
    private dialogService: DialogService,
    private confirmationDialog: ConfirmationDialogService,
    private loadingService: LoadingService,
    private fb: FormBuilder,
    private router: Router,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.formDiretorio = this.createDirectoryForm();
    this.uid = this.route.snapshot.paramMap.get('uid') || '';
    this.isCreateMode = !this.uid || this.uid === 'novo';
    this.pathSelected = this.isCreateMode ? this.translate.instant('Adicionar diretório') : '';
    this.loadCountries();

    if (this.isCreateMode) {
      this.loadRegisteredCountries();
    } else {
      this.getDadosDirectory(this.uid);
    }
  }

  private createDirectoryForm(data?: any): FormGroup {
    return this.fb.group({
      Id: data?.id || '',
      RootPath: data?.rootPath || '',
      Countryid: data?.countryId || '',
      IndexYear: data?.indexYear ?? DIRECTORY_FIXED_INDEXES.IndexYear,
      IndexProvider: data?.indexProvider ?? DIRECTORY_FIXED_INDEXES.IndexProvider,
      IndexMonth: data?.indexMonth ?? DIRECTORY_FIXED_INDEXES.IndexMonth,
      IndexRegion: data?.indexRegion ?? DIRECTORY_FIXED_INDEXES.IndexRegion,
      IndexPDV: data?.indexPDV ?? DIRECTORY_FIXED_INDEXES.IndexPDV,
      IndexPrescriptionBatch: data?.indexPrescriptionBatch ?? DIRECTORY_FIXED_INDEXES.IndexPrescriptionBatch,
      Status: data?.status ?? false,
      FilterYear: data?.filterYear || new Date().getFullYear(),
      FilterMonth: data?.filterMonth || '',
      isLimitPdvs: data?.isLimitPdvs ?? false
    });
  }

  getDadosDirectory(uid: string) {
    this.loadingService.show();
    this.jsonService.getDirectoryDetail(uid).subscribe(
      resp => {
        this.formDiretorio = this.createDirectoryForm({ ...resp, id: uid });
        this.pathSelected = resp.rootPath;
        this.carregarPastasMes();
        this.loadingService.hide();
      },
      erro => {
        console.error(erro);
        this.loadingService.hide();
      }
    );
  }

  loadCountries() {
    this.jsonService.listCountry().subscribe(
      resp => {
        this.jsonPaises = resp;
        this.onCountryChange();
      },
      erro => {
        console.error(erro);
      }
    );
  }

  private loadRegisteredCountries(): void {
    const directories = history.state?.directories;

    if (Array.isArray(directories)) {
      this.setRegisteredCountryIds(directories);
      return;
    }

    this.jsonService.getDirectoryList().subscribe(
      resp => {
        this.setRegisteredCountryIds(resp);
      },
      erro => {
        console.error(erro);
      }
    );
  }

  private setRegisteredCountryIds(directories: Array<any>): void {
    this.registeredCountryIds = new Set(
      directories
        .map(item => item.countryId)
        .filter(countryId => !!countryId)
    );
  }

  onCountryChange(): void {
    if (!this.isCreateMode) {
      return;
    }

    const countryId = this.formDiretorio.value.Countryid;
    if (countryId && this.hasDirectoryForSelectedCountry()) {
      this.notifyCountryAlreadyRegistered();
    }

    const country = this.jsonPaises.find(pais => pais.id === countryId);
    const countryPath = country ? this.normalizePathSegment(country.name) : '';
    const rootPath = countryPath ? `/filiales/${countryPath}/in/CAPTURA GRAFICA/` : '';
    this.formDiretorio.patchValue({ RootPath: rootPath });
    this.pathSelected = rootPath || this.translate.instant('Adicionar diretório');
    this.carregarPastasMes();
  }

  onFilterYearChange(): void {
    this.carregarPastasMes();
  }

  private carregarPastasMes(): void {
    const rootPath = this.formDiretorio.value.RootPath;
    const filterYear = Number(this.formDiretorio.value.FilterYear);

    if (!rootPath || !filterYear) {
      this.mesesDisponiveis = this.mesesFallback;
      return;
    }

    this.jsonService.listMonthFolders(rootPath, filterYear).subscribe({
      next: (pastas) => {
        const atual = this.formDiretorio.value.FilterMonth;
        this.mesesDisponiveis = atual && !pastas.includes(atual) ? [...pastas, atual] : pastas;
      },
      error: (erro) => {
        console.error(erro);
        this.mesesDisponiveis = this.mesesFallback;
      }
    });
  }

  private normalizePathSegment(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '')
      .toLowerCase()
      .trim();
  }

  saveDirectory() {
    if (this.isCreateMode) {
      this.createDirectory();
    } else {
      this.updateDirectory();
    }
  }

  createDirectory() {
    if (!this.validateDirectoryForm()) {
      return;
    }

    this.loadingService.show();
    const formData = this.buildDirectoryFormData(false);

    this.jsonService.createDirectory(formData).subscribe(
      resp => {
        if (this.selectedPdvFile && resp?.id) {
          const pdvFormData = new FormData();
          pdvFormData.append('DirectoryConfigId', resp.id);
          pdvFormData.append('File', this.selectedPdvFile);

          this.jsonService.uploadDirectoryPdv(pdvFormData).pipe(
            finalize(() => this.loadingService.hide())
          ).subscribe({
            next: () => {
              let title = this.translate.instant('Êxito') + '!';
              let message = this.translate.instant('Diretório criado com sucesso!');
              this.dialogService.abrirDialog('aviso', title, message);
              this.router.navigate(['/diretorio-lista']);
            },
            error: (erro) => {
              console.error(erro);
              let title = this.translate.instant('Êxito') + '!';
              let message = this.translate.instant('Diretório criado com sucesso!');
              this.dialogService.abrirDialog('aviso', title, message);
              this.router.navigate(['/diretorio-lista']);
            }
          });
        } else {
          let title = this.translate.instant('Êxito') + '!';
          let message = this.translate.instant('Diretório criado com sucesso!');
          this.dialogService.abrirDialog('aviso', title, message);
          this.loadingService.hide();
          this.router.navigate(['/diretorio-lista']);
        }
      },
      erro => {
        console.error(erro);
        this.loadingService.hide();
      }
    );
  }

  updateDirectory() {
    if (!this.validateDirectoryForm()) {
      return;
    }

    this.loadingService.show();
    const formData = this.buildDirectoryFormData(true);

    this.jsonService.updateDirectory(formData).subscribe(
      resp => {
        let title = this.translate.instant('Êxito') + '!';
        let message = this.translate.instant('Diretório atualizado com sucesso!');
        this.dialogService.abrirDialog('aviso', title, message);
        this.loadingService.hide();
        this.router.navigate(['/diretorio-lista']);
      },
      erro => {
        console.error(erro);
        this.loadingService.hide();
      }
    );
  }

  private validateDirectoryForm(): boolean {
    if (this.hasDirectoryForSelectedCountry()) {
      this.notifyCountryAlreadyRegistered();
      return false;
    }

    if (!this.formDiretorio.value.FilterYear) {
      this.formDiretorio.patchValue({ FilterYear: new Date().getFullYear() });
    }

    const filterYear = Number(this.formDiretorio.value.FilterYear);
    if (!Number.isInteger(filterYear) || filterYear < 1000 || filterYear > 9999) {
      let title = this.translate.instant('Atenção') + '!';
      let message = this.translate.instant('Informe o ano do filtro com 4 dígitos!');
      this.dialogService.abrirDialog('aviso', title, message);
      return false;
    }

    const valuesToValidate = { ...this.formDiretorio.value };
    if (this.isCreateMode) {
      delete valuesToValidate.Id;
    }

    const allFieldsFilled = Object.values(valuesToValidate).every(val => val !== null && val !== '');

    if (!allFieldsFilled) {
      let title = this.translate.instant('Atenção') + '!';
      let message = this.translate.instant('Preencha todos os campos obrigatórios!');
      this.dialogService.abrirDialog('aviso', title, message);
      return false;
    }

    return true;
  }

  private hasDirectoryForSelectedCountry(): boolean {
    const countryId = this.formDiretorio.value.Countryid;
    return this.isCreateMode && !!countryId && this.registeredCountryIds.has(countryId);
  }

  private notifyCountryAlreadyRegistered(): void {
    let title = this.translate.instant('Atenção') + '!';
    let message = this.translate.instant('Já existe um diretório cadastrado para este país.');
    this.dialogService.abrirDialog('aviso', title, message);
  }

  private buildDirectoryFormData(includeId: boolean): FormData {
    const formData = new FormData();
    Object.entries(this.formDiretorio.value).forEach(([key, value]) => {
      if ((includeId || key !== 'Id') && value !== null && value !== undefined) {
        formData.append(key, value.toString());
      }
    });

    return formData;
  }

  async onPdvFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    this.selectedPdvFile = input.files?.[0] || null;
    this.selectedPdvFileCount = null;

    if (!this.selectedPdvFile) {
      return;
    }

    try {
      this.selectedPdvFileCount = await this.countPdvsFromFile(this.selectedPdvFile);
    } catch (error) {
      console.error(error);
      let title = this.translate.instant('Atenção') + '!';
      let message = this.translate.instant('Não foi possível ler o arquivo selecionado.');
      this.dialogService.abrirDialog('aviso', title, message);
    }
  }

  downloadPdvTemplate(): void {
    this.jsonService.downloadPdvTemplate().subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'pdv_template.csv';
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {}
    });
  }

  async uploadDirectoryPdv(): Promise<void> {
    if (this.isCreateMode || this.uploadingPdv) {
      return;
    }

    if (!this.selectedPdvFile) {
      let title = this.translate.instant('Atenção') + '!';
      let message = this.translate.instant('Selecione um arquivo para upload!');
      this.dialogService.abrirDialog('aviso', title, message);
      return;
    }

    let totalPdvs = this.selectedPdvFileCount;
    try {
      totalPdvs = totalPdvs ?? await this.countPdvsFromFile(this.selectedPdvFile);
    } catch (error) {
      console.error(error);
      let title = this.translate.instant('Atenção') + '!';
      let message = this.translate.instant('Não foi possível ler o arquivo selecionado.');
      this.dialogService.abrirDialog('aviso', title, message);
      return;
    }

    const title = this.translate.instant('Confirmação');
    const message = this.translate.instant(
      'Serão inseridos {{count}} PDVs. Esta operação não poderá ser desfeita.',
      { count: totalPdvs }
    );
    const confirmed = await this.confirmationDialog.confirm(title, message);

    if (!confirmed) {
      return;
    }

    const formData = new FormData();
    formData.append('DirectoryConfigId', this.uid);
    formData.append('File', this.selectedPdvFile);

    this.uploadingPdv = true;
    this.loadingService.show();
    this.jsonService.uploadDirectoryPdv(formData).pipe(
      finalize(() => {
        this.uploadingPdv = false;
        this.loadingService.hide();
      })
    ).subscribe(
      resp => {
        let title = this.translate.instant('Êxito') + '!';
        let message = this.translate.instant('Arquivo enviado com sucesso!');
        this.dialogService.abrirDialog('aviso', title, message);
      },
      erro => {
        console.error(erro);
      }
    );
  }

  private countPdvsFromFile(file: File): Promise<number> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const content = reader.result?.toString() || '';
        const total = content
          .split(/\r?\n/)
          .map(line => line.trim())
          .filter(line => line.length > 0).length;

        resolve(total);
      };

      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    });
  }
}
